/**
 * Video Story on the canvas: the dialog, laying a storyboard out as nodes,
 * the music request, watching every scene land, and building the timeline.
 *
 * Kept out of the workspace page like the editor hook. Shooting itself is the
 * page's own plan machinery (`applyPlan` = handlePlanApply), so room scenes
 * pose the 3D studio through the capture queue and every clip waits for its
 * still exactly as a plan from the agent would.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { CsRecipe } from '../../../../common/models/content-studio.model';
import { CanvasNode, CanvasVideoEditNode } from '../canvas/canvas-graph.types';
import {
    CompiledPlan,
    CompiledVideoStory,
    ExistingNode,
    PlannerProduct,
    compilePlan,
} from '../planner/plan-compiler';
import { AspectRatio, createEmptyComposition } from './composition';
import { buildCompositionFromStoryboard } from './templates';
import { loadVideoBrand } from './brand';
import {
    Storyboard,
    StoryboardRequest,
    StoryProgress,
    VideoStoryState,
    cleanWebsite,
    linkScenes,
    readStoryboard,
    storyboardSeconds,
    storyboardToPlan,
    storyProgress,
    storyScenesForAssembly,
} from './video-story';
import { StoryboardDialog } from './StoryboardDialog';

/**
 * Plan columns are 420 apart (plan-compiler COLUMN_GAP): product, still, clip
 * recipe; their pictures spawn in the two columns after that. The timeline
 * sits past them, so its lineage reads left to right without crossing.
 */
const TIMELINE_COLUMN_X = 5 * 420 + 80;

export interface VideoStoryDeps {
    projectUuid?: string;
    nodes: CanvasNode[];
    nodesRef: React.MutableRefObject<CanvasNode[]>;
    setNodes: React.Dispatch<React.SetStateAction<CanvasNode[]>>;
    recipes: CsRecipe[];
    existingNodes: Map<string, ExistingNode>;
    loadProducts: (uuids: string[]) => Promise<PlannerProduct[]>;
    mentionableProducts: PlannerProduct[];
    applyPlan: (compiled: CompiledPlan, summary: string, generate: boolean) => void;
    /** videoEdit.createNode — drops a placeholder node while a planned story is written. */
    createVideoEditNode: (composition: any, opts?: { anchor?: { x: number; y: number }; open?: boolean }) => string;
}

export function useVideoStory(deps: VideoStoryDeps) {
    const api = useContentStudioApi();
    const depsRef = useRef(deps);
    depsRef.current = deps;
    const [dialog, setDialog] = useState<{ productUuids: string[] } | null>(null);
    const musicInFlight = useRef<Set<string>>(new Set());
    /**
     * Each music answer, kept here as well as on the node. Other canvas writes
     * can put back a copy of the nodes from before the answer arrived (music
     * still "pending"); the watcher re-applies it from here instead of
     * concluding the request was lost.
     */
    const musicResults = useRef<Map<string, NonNullable<VideoStoryState['music']>>>(new Map());
    const assembling = useRef<Set<string>>(new Set());

    const patchStory = useCallback((nodeId: string, patch: Partial<VideoStoryState>) => {
        depsRef.current.setNodes((prev) => prev.map((n) => (n.id === nodeId && n.kind === 'video-edit'
            ? { ...n, story: { ...((n as CanvasVideoEditNode).story as VideoStoryState), ...patch } }
            : n)));
    }, []);

    const writeStoryboard = useCallback(async (req: StoryboardRequest): Promise<Storyboard> => {
        const raw = await api.createStoryboard(req);
        const sb = readStoryboard(raw);
        return { ...sb, aspect: sb.aspect || req.aspect || '9:16' };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const requestMusic = useCallback((nodeId: string, sb: Storyboard) => {
        if (!sb.music?.prompt?.trim()) return;
        musicInFlight.current.add(nodeId);
        const durationSec = Math.ceil(Math.max(sb.music.durationSec || 0, storyboardSeconds(sb) + 3));
        api.generateMusic({ prompt: sb.music.prompt, durationSec })
            .then((m) => {
                const music = { status: 'ready' as const, url: m.url, durationSec: m.durationSec ?? durationSec };
                musicResults.current.set(nodeId, music);
                patchStory(nodeId, { music });
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.warn('[video-story] music failed — the video is cut without it', err);
                musicResults.current.set(nodeId, { status: 'failed' });
                patchStory(nodeId, { music: { status: 'failed' } });
            })
            .finally(() => musicInFlight.current.delete(nodeId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patchStory]);

    /**
     * Lay a storyboard out and start shooting. `replaceNodeId` swaps in for a
     * placeholder (the planner flow puts one down while the board is written).
     * Resolves to the video-edit node's id.
     */
    const createFromStoryboard = useCallback(async (
        sb: Storyboard,
        opts: { request?: StoryboardRequest; replaceNodeId?: string; generate?: boolean } = {},
    ): Promise<string> => {
        const d = depsRef.current;
        const plan = storyboardToPlan(sb);
        const products = await d.loadProducts(plan.productUuids);
        const compiled = compilePlan(
            plan,
            new Map(products.map((p) => [p.uuid, p])),
            d.recipes,
            { x: 0, y: 0 },
            d.existingNodes,
            d.nodesRef.current,
        );
        const scenes = linkScenes(sb, compiled.steps);
        if (scenes.length === 0) {
            throw new Error(compiled.dropped[0]?.reason || 'None of the scenes could be set up');
        }
        const id = opts.replaceNodeId || `ved-${uuidv4().slice(0, 8)}`;
        const story: VideoStoryState = {
            phase: 'running',
            request: opts.request,
            storyboard: sb,
            scenes,
            music: sb.music?.prompt?.trim() && opts.generate !== false ? { status: 'pending' } : { status: 'skipped' },
            startedAt: new Date().toISOString(),
        };
        const node: CanvasVideoEditNode = {
            kind: 'video-edit',
            id,
            x: TIMELINE_COLUMN_X,
            y: 0,
            composition: createEmptyComposition((sb.aspect || '9:16') as AspectRatio),
            sourceNodeIds: scenes.map((s) => s.videoNodeId),
            generationNodeIds: [],
            story,
        };
        compiled.nodes.push(node);
        if (opts.replaceNodeId) {
            d.setNodes((prev) => prev.filter((n) => n.id !== opts.replaceNodeId));
        }
        d.applyPlan(compiled, sb.title, opts.generate !== false);
        if (story.music?.status === 'pending') requestMusic(id, sb);
        return id;
    }, [requestMusic]);

    /** Brief → storyboard → canvas, with no dialog (dev hook, planner op). */
    const runStory = useCallback(async (req: StoryboardRequest, opts: { generate?: boolean } = {}): Promise<string> => {
        const d = depsRef.current;
        const placeholder = d.createVideoEditNode(createEmptyComposition((req.aspect || '9:16') as AspectRatio), {});
        patchStory(placeholder, { phase: 'writing', request: req, scenes: [], startedAt: new Date().toISOString() } as VideoStoryState);
        try {
            const sb = await writeStoryboard(req);
            return await createFromStoryboard(sb, { request: req, replaceNodeId: placeholder, generate: opts.generate });
        } catch (err: any) {
            patchStory(placeholder, { phase: 'failed', error: err?.message || String(err) });
            throw err;
        }
    }, [createFromStoryboard, patchStory, writeStoryboard]);

    /** A plan from the agent asked for a video story. */
    const runPlanned = useCallback((vs: CompiledVideoStory, generate: boolean) => {
        void runStory({
            brief: vs.brief,
            productUuids: vs.productUuids,
            aspect: vs.aspect as AspectRatio,
            targetSec: vs.durationSec,
        }, { generate }).catch(() => undefined);
    }, [runStory]);

    /** Build the timeline from whatever has landed. */
    const assemble = useCallback(async (nodeId: string) => {
        if (assembling.current.has(nodeId)) return;
        const d = depsRef.current;
        const node = d.nodesRef.current.find((n) => n.id === nodeId && n.kind === 'video-edit') as CanvasVideoEditNode | undefined;
        const story = node?.story;
        if (!node || !story?.storyboard) return;
        assembling.current.add(nodeId);
        try {
            const sb = story.storyboard;
            const progress = storyProgress(story, d.nodesRef.current);
            const music = musicResults.current.get(nodeId) || story.music;
            const scenes = storyScenesForAssembly(sb, progress);
            if (scenes.length === 0) {
                patchStory(nodeId, { phase: 'failed', error: 'No scene could be shot' });
                return;
            }
            const brand = { ...(await loadVideoBrand(api.getBrandKit)) };
            if (sb.endCard?.tagline) brand.tagline = sb.endCard.tagline;
            const site = cleanWebsite(sb.endCard?.url);
            if (site) brand.website = site;
            const composition = buildCompositionFromStoryboard(scenes, {
                aspect: (sb.aspect || '9:16') as AspectRatio,
                brand,
                captionPreset: (sb.captionPreset as any) || 'editorial-serif',
                music: music?.status === 'ready' && music.url
                    ? { src: music.url, durationSec: music.durationSec, volume: 0.7, name: sb.title }
                    : undefined,
            });
            // Draw the lineage from the clips actually cut in, not the recipes.
            const usedAssets = new Set(scenes.map((s) => s.assetUuid).filter(Boolean));
            const usedNodes = d.nodesRef.current
                .filter((n) => n.kind === 'generation' && usedAssets.has((n as any).asset?.uuid))
                .map((n) => n.id);
            d.setNodes((prev) => prev.map((n) => (n.id === nodeId && n.kind === 'video-edit'
                ? {
                    ...n,
                    composition,
                    sourceNodeIds: usedNodes.length ? usedNodes : (n as CanvasVideoEditNode).sourceNodeIds,
                    story: { ...((n as CanvasVideoEditNode).story as VideoStoryState), assembledAt: new Date().toISOString() },
                }
                : n)));
        } finally {
            assembling.current.delete(nodeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patchStory]);

    // Watch the canvas: assemble each story once every scene has settled.
    useEffect(() => {
        for (const n of deps.nodes) {
            if (n.kind !== 'video-edit') continue;
            const story = (n as CanvasVideoEditNode).story;
            if (!story || story.phase !== 'running' || story.assembledAt) continue;
            if (story.music?.status === 'pending' && !musicInFlight.current.has(n.id)) {
                // Answered, then overwritten by a stale write: put the answer back.
                // Never answered here (a reload lost the request): cut without it.
                patchStory(n.id, { music: musicResults.current.get(n.id) || { status: 'failed' } });
                continue;
            }
            if (storyProgress(story, deps.nodes).readyToAssemble) void assemble(n.id);
        }
    }, [deps.nodes, assemble, patchStory]);

    const progressFor = useCallback((node: CanvasVideoEditNode): StoryProgress | null => {
        if (!node.story) return null;
        return storyProgress(node.story, depsRef.current.nodes);
    }, []);

    const stateFor = useCallback((nodeId: string) => {
        const node = depsRef.current.nodesRef.current.find((n) => n.id === nodeId && n.kind === 'video-edit') as CanvasVideoEditNode | undefined;
        if (!node?.story) return null;
        const p = storyProgress(node.story, depsRef.current.nodesRef.current);
        return {
            phase: p.phase,
            total: p.total,
            shot: p.shot,
            animated: p.animated,
            failed: p.failed,
            music: node.story.music?.status,
            assembled: !!node.story.assembledAt,
            error: node.story.error,
            title: node.story.storyboard?.title,
            scenes: p.scenes.map((s) => ({
                sceneId: s.sceneId,
                image: s.image?.url,
                video: s.video?.url,
                imageFailed: s.imageFailed,
                videoFailed: s.videoFailed,
            })),
        };
    }, []);

    const dialogElement = deps.projectUuid ? (
        <StoryboardDialog
            open={!!dialog}
            onClose={() => setDialog(null)}
            products={deps.mentionableProducts}
            initialProductUuids={dialog?.productUuids}
            recipes={deps.recipes}
            onWrite={writeStoryboard}
            onCreate={async (sb, req) => { await createFromStoryboard(sb, { request: req }); }}
            uploadFrame={async (file) => (await api.uploadStyleRef(file)).url}
        />
    ) : null;

    return {
        openDialog: (productUuids: string[] = []) => setDialog({ productUuids }),
        dialog: dialogElement,
        runStory,
        runPlanned,
        createFromStoryboard,
        assemble,
        progressFor,
        stateFor,
    };
}
